import { useId, type ReactElement, type ReactNode } from 'react';
import { Icon } from './Icon';

/**
 * Label, control, help and error of one form field, linked for screen readers (the control gets `id` and
 * `aria-describedby`). `required` adds a marker after the label (the control itself still sets aria-required).
 */
export function Field({ label, help, error, children, className = '', required }: { label: ReactNode; help?: ReactNode; error?: string | null; children: (id: string, describedBy: string | undefined) => ReactElement; className?: string; required?: boolean }) {
  const id = useId();
  const helpId = help ? `${id}-help` : undefined;
  const errId = error ? `${id}-err` : undefined;
  return (
    <div className={`field ${error ? 'has-error' : ''} ${className}`}>
      <label htmlFor={id}>
        {label}
        {required && (
          <span className="req" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children(id, [helpId, errId].filter(Boolean).join(' ') || undefined)}
      {help && <span className="field-help" id={helpId}>{help}</span>}
      {error && (
        <span className="field-error" id={errId}>
          <Icon name="warn" size={15} />
          <span>{error}</span>
        </span>
      )}
    </div>
  );
}
